import createReducer from "./createReducer";
import { SUPPRESS_SUCCESS_GROWL, SUPPRESS_ALL_GROWL } from "utils/constants";
import { getLibraryKpis, getAvailableKpis } from "selectors/PredictiveScoring";
import { isVcrOnlyNonVideoMismatch } from "widgets/actions/PredictiveScoringAction";

const SCORING_HEADER = "Predictive Scoring";
const VCR_NON_VIDEO_MESSAGE = "Video Completion Rate KPI is only available for Video Creatives. Any non-Video Creatives will not return a VCR Score.";
const NO_KPIS_MESSAGE = "There are no KPIs set for this library. Set at least one KPI in Library Settings before starting predictive scoring.";

// CRM's BadRequestException gives every 400 from this endpoint the same code/name ("Bad Request");
// description is the only field that varies, so this must stay in sync with
// PredictiveScoringHandler.ERROR_NO_KPIS_CONFIGURED in j-creative-manager-api.
function isNoKpisConfiguredError(response) {
	return response?.data?.description === "No KPIs configured for this library";
}

function scoringMessage(type, message) {
	return { type, id: new Date().getTime(), messageHeader: SCORING_HEADER, message };
}

const initialState = {
	libraryKpis: [],
	availableKpis: [],
	libraryKpisLibraryId: null,
	scores: {},
	scoresRequestKey: null,
	scoreDetail: null,
	loading: false,
	error: null,
};

function buildScoresRequestKey(libraryId, entityType, creativeId) {
	return `${libraryId}:${entityType}:${creativeId ?? ""}`;
}

export const PREDICTIVE_SCORE_ENTITY_TYPES = Object.freeze({
	CREATIVE: "CREATIVE",
	VERSION: "VERSION",
});

export const PREDICTIVE_SCORING_ACTIONS = {
	LOAD_LIBRARY_KPIS_AJAX: "PREDICTIVE_SCORING_LOAD_LIBRARY_KPIS_AJAX",
	LOAD_LIBRARY_KPIS_SUCCESS: "PREDICTIVE_SCORING_LOAD_LIBRARY_KPIS_SUCCESS",
	LOAD_LIBRARY_KPIS_ERROR: "PREDICTIVE_SCORING_LOAD_LIBRARY_KPIS_ERROR",

	SAVE_LIBRARY_KPIS_AJAX: "PREDICTIVE_SCORING_SAVE_LIBRARY_KPIS_AJAX",
	SAVE_LIBRARY_KPIS_SUCCESS: "PREDICTIVE_SCORING_SAVE_LIBRARY_KPIS_SUCCESS",
	SAVE_LIBRARY_KPIS_ERROR: "PREDICTIVE_SCORING_SAVE_LIBRARY_KPIS_ERROR",

	LOAD_SCORES_AJAX: "PREDICTIVE_SCORING_LOAD_SCORES_AJAX",
	LOAD_SCORES_SUCCESS: "PREDICTIVE_SCORING_LOAD_SCORES_SUCCESS",
	LOAD_SCORES_ERROR: "PREDICTIVE_SCORING_LOAD_SCORES_ERROR",

	INITIATE_SCORING_AJAX: "PREDICTIVE_SCORING_INITIATE_AJAX",
	INITIATE_SCORING_SUCCESS: "PREDICTIVE_SCORING_INITIATE_SUCCESS",
	INITIATE_SCORING_ERROR: "PREDICTIVE_SCORING_INITIATE_ERROR",

	LOAD_SCORE_DETAIL_AJAX: "PREDICTIVE_SCORING_LOAD_DETAIL_AJAX",
	LOAD_SCORE_DETAIL_SUCCESS: "PREDICTIVE_SCORING_LOAD_DETAIL_SUCCESS",
	LOAD_SCORE_DETAIL_ERROR: "PREDICTIVE_SCORING_LOAD_DETAIL_ERROR",

	EXPORT_SCORES_AJAX: "PREDICTIVE_SCORING_EXPORT_AJAX",
	EXPORT_SCORES_SUCCESS: "PREDICTIVE_SCORING_EXPORT_SUCCESS",
	EXPORT_SCORES_ERROR: "PREDICTIVE_SCORING_EXPORT_ERROR",

	CLEAR_SCORE_DETAIL: "PREDICTIVE_SCORING_CLEAR_DETAIL",
};

export function loadLibraryKpis(libraryId) {
	return (dispatch) => {
		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_AJAX,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/predictive-scoring/kpis`,
				success: PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_ERROR,
			},
			// libraryId travels with the request (not just the eventual response) so the reducer can
			// clear out a previous library's stale libraryKpis/availableKpis the instant a fetch starts
			// for a different one, rather than leaving them visible until this fetch resolves.
			meta: { ...SUPPRESS_SUCCESS_GROWL, libraryId },
		});
	};
}

export function saveLibraryKpis(libraryId, kpis) {
	return (dispatch) => {
		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.SAVE_LIBRARY_KPIS_AJAX,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/predictive-scoring/kpis`,
				method: "POST",
				data: { kpis },
				success: PREDICTIVE_SCORING_ACTIONS.SAVE_LIBRARY_KPIS_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.SAVE_LIBRARY_KPIS_ERROR,
			},
			// Library Settings save already shows its own success growl via updateLibrary; this call
			// piggybacks on the same submit and would otherwise pop a second, redundant "Success" growl.
			meta: SUPPRESS_SUCCESS_GROWL,
		}).then(() => dispatch(loadLibraryKpis(libraryId)));
	};
}

export function loadScores(libraryId, entityType, options = {}) {
	const params = new URLSearchParams({ entityType });
	if (options.creativeId) { params.append("creativeId", options.creativeId); }
	if (options.entityIds) { params.append("entityIds", options.entityIds); }
	if (options.skipCache) { params.append("skipCache", "true"); }

	const requestKey = buildScoresRequestKey(libraryId, entityType, options.creativeId);

	return (dispatch) => {
		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_AJAX,
			AJAX: {
				url: `predictive-scoring/library/${libraryId}/scores?${params.toString()}`,
				success: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_ERROR,
			},
			meta: { ...SUPPRESS_ALL_GROWL, requestKey },
		});
	};
}

export function initiateScoring(libraryId, entityType, entityIds, adTypes = []) {
	return (dispatch, getState) => {
		const state = getState();
		const isVcrNonVideoMismatch = isVcrOnlyNonVideoMismatch(adTypes, getLibraryKpis(state), getAvailableKpis(state));

		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.INITIATE_SCORING_AJAX,
			AJAX: {
				// libraryId is in the path; the backend authorizes on it (Creative Library / Edit).
				url: `predictive-scoring/library/${libraryId}/request`,
				method: "POST",
				data: { entityType, entityIds },
				success: PREDICTIVE_SCORING_ACTIONS.INITIATE_SCORING_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.INITIATE_SCORING_ERROR,
			},
			// The request only returns 202 Accepted — scoring runs async and can still fail. Don't let the
			// generic AJAX "Success" growl imply the scores are ready; say it was submitted instead. The
			// real outcome arrives later via the socket notification (Complete / Partial / Failed).
			meta: {
				entityIds,
				buildMessage(response, success) {
					if (success) {
						return scoringMessage("info", "Sent for predictive scoring. You'll be notified when scoring completes.");
					}
					if (isNoKpisConfiguredError(response)) {
						return scoringMessage("error", NO_KPIS_MESSAGE);
					}
					if (isVcrNonVideoMismatch) {
						return scoringMessage("error", VCR_NON_VIDEO_MESSAGE);
					}
					return scoringMessage("error", "Couldn't start predictive scoring. Please try again.");
				},
			},
		});
	};
}

export function loadScoreDetail(entityType, entityId) {
	const path = entityType === PREDICTIVE_SCORE_ENTITY_TYPES.CREATIVE ? `predictive-scoring/creative/${entityId}` : `predictive-scoring/version/${entityId}`;

	return (dispatch) => {
		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORE_DETAIL_AJAX,
			AJAX: {
				url: path,
				success: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORE_DETAIL_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.LOAD_SCORE_DETAIL_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
		});
	};
}

export function exportScores(libraryId, entityType, entityIds) {
	return (dispatch) => {
		return dispatch({
			type: PREDICTIVE_SCORING_ACTIONS.EXPORT_SCORES_AJAX,
			AJAX: {
				url: "predictive-scoring/export",
				method: "POST",
				data: { entityType, libraryId, ...(entityIds?.length ? { entityIds } : {}) },
				responseType: "blob",
				success: PREDICTIVE_SCORING_ACTIONS.EXPORT_SCORES_SUCCESS,
				error: PREDICTIVE_SCORING_ACTIONS.EXPORT_SCORES_ERROR,
			},
		});
	};
}

export function clearScoreDetail() {
	return { type: PREDICTIVE_SCORING_ACTIONS.CLEAR_SCORE_DETAIL };
}

const handlers = {
	[PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_AJAX]: (state, action) => {
		const requestedLibraryId = action.meta?.libraryId;
		// A fetch just started for a different library than the one libraryKpis currently belongs to
		// (or none was tracked yet) — clear the stale data now instead of leaving it visible until this
		// request resolves, which previously let a just-picked advertiser briefly (or persistently, on
		// a slow/failed request) show a totally unrelated library's KPIs as if they were its own.
		if (requestedLibraryId !== state.libraryKpisLibraryId) {
			return { ...state, libraryKpis: [], availableKpis: []};
		}
		return state;
	},

	[PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_SUCCESS]: (state, action) => ({
		...state,
		libraryKpis: action.payload.data.kpis || [],
		availableKpis: action.payload.data.availableKpis || [],
		libraryKpisLibraryId: action.meta?.libraryId ?? state.libraryKpisLibraryId,
	}),

	[PREDICTIVE_SCORING_ACTIONS.LOAD_LIBRARY_KPIS_ERROR]: (state, action) => ({
		...state,
		libraryKpis: [],
		availableKpis: [],
		libraryKpisLibraryId: action.meta?.libraryId ?? state.libraryKpisLibraryId,
	}),

	[PREDICTIVE_SCORING_ACTIONS.SAVE_LIBRARY_KPIS_SUCCESS]: state => ({
		...state,
	}),

	[PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_SUCCESS]: (state, action) => {
		if (action.meta?.requestKey !== state.scoresRequestKey) { return state; }
		return {
			...state,
			scores: buildScoresMap(action.payload.data),
			loading: false,
		};
	},

	[PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_AJAX]: (state, action) => ({
		...state,
		loading: true,
		scoresRequestKey: action.meta?.requestKey ?? state.scoresRequestKey,
	}),

	[PREDICTIVE_SCORING_ACTIONS.LOAD_SCORES_ERROR]: (state, action) => {
		if (action.meta?.requestKey !== state.scoresRequestKey) { return state; }
		return {
			...state,
			loading: false,
			error: "Failed to load scores",
		};
	},

	[PREDICTIVE_SCORING_ACTIONS.INITIATE_SCORING_SUCCESS]: (state, action) => {
		const entityIds = action.meta?.entityIds || [];
		const processingEntries = {};
		entityIds.forEach((id) => {
			processingEntries[id] = { ...(state.scores[id] || {}), entityId: id, status: "PROCESSING" };
		});
		return {
			...state,
			scores: { ...state.scores, ...processingEntries },
		};
	},

	[PREDICTIVE_SCORING_ACTIONS.LOAD_SCORE_DETAIL_SUCCESS]: (state, action) => ({
		...state,
		scoreDetail: action.payload.data,
	}),

	[PREDICTIVE_SCORING_ACTIONS.CLEAR_SCORE_DETAIL]: state => ({
		...state,
		scoreDetail: null,
	}),
};

function buildScoresMap(payload) {
	if (!payload || !payload.scores) { return {}; }
	const map = {};
	payload.scores.forEach((entry) => {
		map[entry.entityId] = entry;
	});
	return map;
}

export default createReducer(initialState, handlers);
